import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, useLocation, useNavigate, } from 'react-router-dom';
import Login from './components/Login';
import BooksList from './components/BooksList';
import Home from './pages/Home';
import About from './pages/About';
import Oled from './pages/Oled';
import Flat from './pages/Flat';
import Curved from './pages/Curved';
import ImageGallery from './pages/ImageGallery';
import GalleryDeviceLog from './pages/GalleryDeviceLog';
import NotFound from './pages/errors/NotFound';
import NotFoundRedirect from './pages/errors/NotFoundRedirect';
import ServerError from './pages/errors/ServerError';
import AppErrorBoundary from './components/AppErrorBoundary';
import WindLogo from './components/WindLogo';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

/** 产品页 /oled 与 gallery、日志页 /gallery/oled/... 共用同一导航高亮 */
function isCategoryNavActive(categoryKey, pathname) {
  if (pathname === `/${categoryKey}`) {
    return true;
  }
  const galleryPrefix = `/gallery/${categoryKey}`;
  return pathname === galleryPrefix || pathname.startsWith(`${galleryPrefix}/`);
}

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, username, logout, ready } = useAuth();
  const isLoginPage = location.pathname === '/login';
  const isGalleryPage = /^\/gallery\//.test(location.pathname);

  const getCategoryNavClass = (categoryKey) => ({ isActive }) =>
    isActive || isCategoryNavActive(categoryKey, location.pathname)
      ? 'nav-link active'
      : 'nav-link';

  const getCategoryNavAria = (categoryKey) =>
    isCategoryNavActive(categoryKey, location.pathname) ? 'page' : undefined;
  const ProtectedRoute = ({ children }) => {
    if (!ready) {
      return <div className="loading">加载中...</div>;
    }
    if (isAuthenticated) {
      return children;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  };
  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };
  return (
    <div className={`App${isLoginPage ? ' app-login' : ''}${isGalleryPage ? ' app-gallery' : ''}`}>
      <header className="site-header">
        <nav className="menu">
          <NavLink to="/" className="site-brand site-brand--logo" title="返回首页" end><WindLogo /></NavLink>
          <div className="nav-actions">
            <ul>
              <li>
              <NavLink to="/" className="nav-link" title="返回首页" end>首页</NavLink>
              </li>
              <li>
              <NavLink
                to="/oled"
                className={getCategoryNavClass('oled')}
                title="OLED"
                aria-current={getCategoryNavAria('oled')}
              >
                OLED
              </NavLink>
              </li>
              <li>
              <NavLink
                to="/flat"
                className={getCategoryNavClass('flat')}
                title="直面屏"
                aria-current={getCategoryNavAria('flat')}
              >
                直面屏
              </NavLink>
              </li>
              <li>
              <NavLink
                to="/curved"
                className={getCategoryNavClass('curved')}
                title="曲面屏"
                aria-current={getCategoryNavAria('curved')}
              >
                曲面屏
              </NavLink>
              </li>
              <li>
              <NavLink to="/product" className="nav-link" title="商城">商城</NavLink>
              </li>
              <li>
              <NavLink to="/about" className="nav-link" title="关于">关于</NavLink>
              </li>
            </ul>
            <div className="nav-user">
              {isAuthenticated ? (
                <>
                  <span className="nav-username" title={username || ''}>
                    您好，{username || '用户'}
                  </span>
                  <button type="button" className="nav-logout-btn" onClick={handleLogout}>
                    退出登录
                  </button>
                </>
              ) : isLoginPage ? (
                <span className="nav-link nav-link--login nav-link--login-current" aria-current="page">登录</span>
              ) : (
                <NavLink to="/login" className="nav-link nav-link--login" state={{ from: location }} title="登录">登录</NavLink>
              )}
            </div>
          </div>
        </nav>
      </header>

      <div className={`content${isGalleryPage ? ' content--gallery' : ''}`}>
        <main className="main-area">
          <Routes>
            <Route path="/gallery/:category/log/:deviceId" element={<ProtectedRoute><GalleryDeviceLog /></ProtectedRoute>} />
            <Route path="/gallery/:category" element={<ProtectedRoute><ImageGallery /></ProtectedRoute>} />
            <Route path="/about" element={<About />} />
            <Route path="/oled" element={<Oled />} />
            <Route path="/flat" element={<Flat />} />
            <Route path="/curved" element={<Curved />} />
            <Route path="/product" element={<ProtectedRoute><BooksList /></ProtectedRoute>} />
             <Route path="/login" element={<Login />} />
            <Route path="/" element={<Home />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="/500" element={<ServerError />} />
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppErrorBoundary>
          <AppLayout />
        </AppErrorBoundary>
      </AuthProvider>
    </Router>
  );
}
export default App;
